import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { handleErrorNotification } from '@utils/error';
import { StatusCodes, UserRoles } from '@constants/enum/common';
import { addNotification } from '@redux/actions/notificationActions';
import { RootState } from '@redux/store';
import { NotificationType } from '@redux/types/types';
import { IAPIPaginationData } from '@redux/types/types';
import {
  createApiKeyApi,
  deleteApiKeyApi,
  fetchApiKeysApi,
  IApiKeysParams,
  updateApiKeyApi,
} from '../api/apiKeys';
import { API_KEY_ACTION_MESSAGES, AsyncActionType } from '../constants/apiKeys';
import { IApiKey, ICreateApiKeyPayload, IUpdateApiKeyPayload } from '../types/apiKeys';

interface ApiKeysState {
  apiKeys: IAPIPaginationData<IApiKey[]>;
  apiKey: IApiKey;
  apiToken: string;
  createApiKeyStatus: StatusCodes;
  deleteApiKeyStatus: StatusCodes;
  fetchApiKeysStatus: StatusCodes;
  updateApiKeyStatus: StatusCodes;
}

export const initialState: ApiKeysState = {
  apiKeys: {
    data: [],
    total: 0,
    has_more: false,
  },
  apiKey: {
    api_token_id: '',
    masked_token: '',
    name: '',
    expires: true,
    expires_at: '',
    user: {
      given_name: '',
      family_name: '',
    },
    role: UserRoles.EDITOR,
    all_workspaces: true,
    workspaces: [],
    personal: true,
  },
  apiToken: '',
  createApiKeyStatus: StatusCodes.IDLE,
  deleteApiKeyStatus: StatusCodes.IDLE,
  fetchApiKeysStatus: StatusCodes.IDLE,
  updateApiKeyStatus: StatusCodes.IDLE,
};

export const createApiKey = createAsyncThunk(
  AsyncActionType.CREATE_API_KEY,
  async (payload: ICreateApiKeyPayload, { dispatch, rejectWithValue }) => {
    try {
      const response = await createApiKeyApi<{ api_token: string }>(payload);
      dispatch(
        addNotification({
          content: API_KEY_ACTION_MESSAGES.CREATED,
          type: NotificationType.Success,
        }),
      );
      return response.data;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const deleteApiKey = createAsyncThunk(
  AsyncActionType.DELETE_API_KEY,
  async (apiKeyId: string, { dispatch, rejectWithValue }) => {
    try {
      const response = await deleteApiKeyApi<void>(apiKeyId);
      dispatch(
        addNotification({
          content: API_KEY_ACTION_MESSAGES.DELETED,
          type: NotificationType.Success,
        }),
      );
      return response.data;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const fetchApiKeys = createAsyncThunk(
  AsyncActionType.FETCH_API_KEYS,
  async (payload: IApiKeysParams, { dispatch, rejectWithValue }) => {
    try {
      const response = await fetchApiKeysApi<IAPIPaginationData<IApiKey[]>>(payload);
      return response.data;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const updateApiKey = createAsyncThunk(
  AsyncActionType.UPDATE_API_KEY,
  async (payload: IUpdateApiKeyPayload & { apiKeyId: string }, { dispatch, rejectWithValue }) => {
    const { apiKeyId, ...data } = payload;
    try {
      const response = await updateApiKeyApi(apiKeyId, data);
      dispatch(
        addNotification({
          content: API_KEY_ACTION_MESSAGES.UPDATED,
          type: NotificationType.Success,
        }),
      );
      return response.data;
    } catch (error) {
      handleErrorNotification(error, dispatch);
      return rejectWithValue(error);
    }
  },
);

export const apiKeysSlice = createSlice({
  name: 'apiKeys',
  initialState,
  reducers: {
    setApiKey: (state, action: PayloadAction<IApiKey>) => {
      state.apiKey = action.payload;
    },
  },
  extraReducers: (builder) => {
    // Fetch API Keys
    builder
      .addCase(fetchApiKeys.pending, (state) => {
        state.fetchApiKeysStatus = StatusCodes.IN_PROGRESS;
      })
      .addCase(fetchApiKeys.fulfilled, (state, action) => {
        state.apiKeys = action.payload;
        state.fetchApiKeysStatus = StatusCodes.SUCCESS;
      })
      .addCase(fetchApiKeys.rejected, (state) => {
        state.fetchApiKeysStatus = StatusCodes.ERROR;
      });

    // Create API Key
    builder
      .addCase(createApiKey.pending, (state) => {
        state.createApiKeyStatus = StatusCodes.IN_PROGRESS;
      })
      .addCase(createApiKey.fulfilled, (state, action) => {
        state.apiToken = action.payload.api_token;
        state.createApiKeyStatus = StatusCodes.SUCCESS;
      })
      .addCase(createApiKey.rejected, (state) => {
        state.createApiKeyStatus = StatusCodes.ERROR;
      });

    // Delete API Key
    builder
      .addCase(deleteApiKey.pending, (state) => {
        state.deleteApiKeyStatus = StatusCodes.IN_PROGRESS;
      })
      .addCase(deleteApiKey.fulfilled, (state, action) => {
        const deletedApiKeyId = action.meta.arg;
        state.deleteApiKeyStatus = StatusCodes.SUCCESS;
        state.apiKeys.data = state.apiKeys.data.filter(
          ({ api_token_id: apiKeyId }: IApiKey) => apiKeyId !== deletedApiKeyId,
        );
      })
      .addCase(deleteApiKey.rejected, (state) => {
        state.deleteApiKeyStatus = StatusCodes.ERROR;
      });

    // Update API Key
    builder
      .addCase(updateApiKey.pending, (state) => {
        state.updateApiKeyStatus = StatusCodes.IN_PROGRESS;
      })
      .addCase(updateApiKey.fulfilled, (state) => {
        state.updateApiKeyStatus = StatusCodes.SUCCESS;
      })
      .addCase(updateApiKey.rejected, (state) => {
        state.updateApiKeyStatus = StatusCodes.ERROR;
      });
  },
});

export const { setApiKey } = apiKeysSlice.actions;

export default apiKeysSlice.reducer;

// SELECTORS
const selectApiKeysState = (state: RootState) => state.apiKeysStore;

// Helper function to get a key from the state
const getStoreKey =
  <K extends keyof ApiKeysState>(key: K): ((state: RootState) => ApiKeysState[K]) =>
  (state: RootState) =>
    selectApiKeysState(state)[key];

// Exported selectors
export const selectApiKeys = createSelector([getStoreKey('apiKeys')], (apiKeys) => apiKeys);

export const selectApiKey = createSelector([getStoreKey('apiKey')], (apiKey) => apiKey);

export const selectCreateApiKeyStatus = createSelector(
  [getStoreKey('createApiKeyStatus')],
  (createApiKeyStatus) => createApiKeyStatus,
);

export const selectDeleteApiKeyStatus = createSelector(
  [getStoreKey('deleteApiKeyStatus')],
  (deleteApiKeyStatus) => deleteApiKeyStatus,
);

export const selectFetchApiKeysStatus = createSelector(
  [getStoreKey('fetchApiKeysStatus')],
  (fetchApiKeysStatus) => fetchApiKeysStatus,
);

export const selectUpdateApiKeyStatus = createSelector(
  [getStoreKey('updateApiKeyStatus')],
  (updateApiKeyStatus) => updateApiKeyStatus,
);

export const selectApiToken = createSelector([getStoreKey('apiToken')], (apiToken) => apiToken);
